import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  AlertTriangle,
  BadgeCheck,
  Check,
  ChevronDown,
  Clock,
  Gift,
} from "lucide-react";
import { PACKAGES, MASCOT_FACE_URL, BATHROOM_GROUPS, BATHROOM_VIDEOS } from "@/config/brand";
import { PACKAGE_CONTENT, TRUST_BADGES } from "@/config/packageContent";
import { useTracking } from "@/hooks/useTracking";
import EnquiryForm from "@/components/EnquiryForm";
import { Reveal, MaskLine } from "@/components/Reveal";

const GSTNote = () => (
  <p className="mt-2.5 text-xs font-bold uppercase tracking-[0.22em] text-white/45">
    Fixed Price · Any Bathroom Size · No "We'll Get Back to You"
  </p>
);

const TrustBadges = () => (
  <section data-testid="trust-badges-section" className="border-t border-white/10 py-14 md:py-16">
    <div className="mx-auto max-w-5xl px-5 md:px-10">
      <div className="grid gap-4 sm:grid-cols-3">
        {TRUST_BADGES.map((b, i) => (
          <Reveal key={b.title} delay={0.07 * i}>
            <div
              data-testid={`trust-badge-${b.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className="h-full rounded-2xl border border-sdh-sky/25 bg-sdh-sky/[0.08] p-6"
            >
              <b.icon size={24} className="text-sdh-sky" />
              <h3 className="mt-3.5 font-display text-xl uppercase tracking-wide text-white">
                {b.title}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-white/60">{b.note}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const InclusionsSection = ({ content }) => (
  <section data-testid="package-inclusions-section" className="py-16 md:py-24">
    <div className="mx-auto max-w-5xl px-5 md:px-10">
      <Reveal>
        <p className="text-xs font-bold uppercase tracking-[0.25em] text-sdh-sky">
          What's included
        </p>
        <h2 className="mx-auto mt-3 max-w-3xl text-center font-display text-4xl uppercase leading-[0.95] tracking-tight text-white md:text-6xl">
          {content.heading}
        </h2>
        <p className="mx-auto mt-4 max-w-xl text-center text-base leading-relaxed text-white/70 md:text-lg">
          {content.intro}
        </p>
        <p className="mx-auto mt-3 max-w-md text-center text-sm italic text-white/50">
          Spoiler: it's a lot.
        </p>
      </Reveal>

      <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {content.groups.map((g, i) => (
          <Reveal key={g.title} delay={0.04 * (i % 4)}>
            <div
              data-testid={`inclusion-group-${g.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className="h-full rounded-2xl border border-white/10 bg-white/[0.05] p-5 backdrop-blur-sm transition-colors duration-300 hover:border-sdh-sky/50"
            >
              <div className="flex items-start justify-between gap-2">
                <g.icon size={22} className="shrink-0 text-sdh-sky" />
                {g.badge && (
                  <span
                    data-testid="waterproofing-warranty-badge"
                    className="inline-flex items-center gap-1 rounded-full bg-sdh-sky/15 px-2.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.12em] text-sdh-sky"
                  >
                    <BadgeCheck size={11} />
                    {g.badge}
                  </span>
                )}
              </div>
              <h3 className="mt-3 font-display text-lg uppercase leading-tight tracking-wide text-white">
                {g.title}
              </h3>
              <ul className="mt-2.5 space-y-1.5">
                {g.items.map((item) => (
                  <li key={item} className="flex items-start gap-2 text-[13px] leading-snug text-white/65">
                    <Check size={13} className="mt-0.5 shrink-0 text-sdh-sky" />
                    {item}
                  </li>
                ))}
              </ul>
              {g.note && <p className="mt-2.5 text-[11px] italic text-white/40">{g.note}</p>}
            </div>
          </Reveal>
        ))}
      </div>

      {content.pc && (
        <Reveal delay={0.1}>
          <div
            data-testid="pc-items-section"
            className="mt-8 rounded-2xl border border-sdh-sky/25 bg-sdh-sky/[0.07] p-6 md:p-8"
          >
            <h3 className="font-display text-2xl uppercase tracking-wide text-white">
              {content.pc.label}
            </h3>
            {content.pc.items && (
              <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-5">
                {content.pc.items.map((item) => (
                  <div
                    key={item.label}
                    data-testid={`pc-item-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className="flex flex-col items-center gap-2.5 rounded-xl border border-white/10 bg-[#071735] p-4 text-center"
                  >
                    <item.icon size={22} className="text-sdh-sky" />
                    <span className="text-xs font-semibold text-white/85">{item.label}</span>
                  </div>
                ))}
              </div>
            )}
            <p className="mt-4 text-sm leading-relaxed text-white/60">{content.pc.note}</p>
            <div className="mt-6 rounded-xl border border-white/15 bg-white/[0.04] p-5">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-sdh-sky">
                How it works
              </p>
              <p className="mt-3 text-sm leading-relaxed text-white/75">
                Submit your details below and we'll send you our supplier's catalogue with every
                vanity, toilet, tapware and mirror option included in your package. Pick what
                you love — the prices are already covered, so you just choose the style.
              </p>
            </div>
            <div className="mt-4 rounded-xl border border-white/15 bg-white/[0.04] p-5">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-sdh-sky">
                Tiles
              </p>
              <p className="mt-3 text-sm leading-relaxed text-white/75">
                Choose tiles from any supplier — $30 per square metre allowance. You order
                them, we pay for them, we deliver them. Simple.
              </p>
            </div>
          </div>
        </Reveal>
      )}

      <Reveal delay={0.12}>
        <div data-testid="bonus-section" className="mt-8">
          <div className="flex items-center gap-3">
            <Gift size={22} className="text-sdh-sky" />
            <h3 className="font-display text-2xl uppercase tracking-wide text-white md:text-3xl">
              Included at no extra cost
            </h3>
          </div>
          <div className="mt-5 grid gap-4 md:grid-cols-3">
            {content.bonus.map((b) => (
              <div
                key={b.title}
                data-testid={`bonus-${b.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="rounded-2xl border border-white/10 bg-white/[0.05] p-5"
              >
                <b.icon size={20} className="text-sdh-sky" />
                <h4 className="mt-3 font-display text-lg uppercase tracking-wide text-white">
                  {b.title}
                </h4>
                <p className="mt-2 text-[13px] leading-snug text-white/60">{b.note}</p>
              </div>
            ))}
          </div>
        </div>
      </Reveal>

      <Reveal delay={0.14}>
        <div
          data-testid="unforeseen-section"
          className="mt-8 rounded-2xl border border-white/12 bg-white/[0.04] p-6 md:p-8"
        >
          <div className="flex items-center gap-3">
            <AlertTriangle size={20} className="text-sdh-sky" />
            <h3 className="font-display text-2xl uppercase tracking-wide text-white">
              {content.unforeseen.title}
            </h3>
          </div>
          <p className="mt-3.5 max-w-2xl text-sm leading-relaxed text-white/55">
            {content.unforeseen.note}
          </p>
          <ul className="mt-5 grid gap-2.5 sm:grid-cols-2">
            {content.unforeseen.items.map((item) => (
              <li
                key={item}
                data-testid={`unforeseen-item-${item.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="flex items-start gap-2.5 text-sm font-medium text-white/80"
              >
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-sdh-sky" />
                {item}
              </li>
            ))}
          </ul>
        </div>
      </Reveal>
    </div>
  </section>
);

const GallerySection = () => (
  <section data-testid="package-gallery-section" className="border-t border-white/10 py-16 md:py-20">
    <div className="mx-auto max-w-5xl px-5 md:px-10">
      <Reveal>
        <p className="text-xs font-bold uppercase tracking-[0.25em] text-sdh-sky">
          Our recent work
        </p>
        <h2 className="mx-auto mt-3 max-w-xl text-center font-display text-3xl uppercase leading-tight text-white md:text-5xl">
          Bathrooms we've completed.
        </h2>
        <p className="mx-auto mt-4 max-w-lg text-center text-base text-white/60">
          Real Sydney bathrooms, renovated by our team.
        </p>
      </Reveal>
      <div className="mt-12 space-y-10">
        {BATHROOM_GROUPS.map((photos, gi) => (
          <Reveal key={gi} delay={0.08 * gi}>
            <div
              data-testid={`bathroom-group-${gi}`}
              className="grid gap-4 sm:grid-cols-2 md:grid-cols-3"
            >
              {photos.map((photo, pi) => (
                <div
                  key={photo}
                  data-testid={`gallery-photo-${gi}-${pi}`}
                  className="group overflow-hidden rounded-2xl border border-white/10"
                >
                  <img
                    src={photo}
                    alt={`Completed bathroom renovation ${gi + 1}-${pi + 1}`}
                    className="aspect-[4/3] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]"
                    loading="lazy"
                  />
                </div>
              ))}
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const VideoSection = () => (
  <section data-testid="package-video-section" className="border-t border-white/10 py-16 md:py-20">
    <div className="mx-auto max-w-5xl px-5 md:px-10">
      <Reveal>
        <p className="text-xs font-bold uppercase tracking-[0.25em] text-sdh-sky">
          See it in action
        </p>
        <h2 className="mx-auto mt-3 max-w-xl text-center font-display text-3xl uppercase leading-tight text-white md:text-5xl">
          Walkthrough videos.
        </h2>
        <p className="mx-auto mt-4 max-w-lg text-center text-base text-white/60">
          Real bathrooms, real finishes, real walkthroughs. Tap to watch.
        </p>
      </Reveal>
      <div className="mt-10 grid gap-4 sm:grid-cols-2">
        {BATHROOM_VIDEOS.map((video, i) => (
          <Reveal key={video} delay={0.06 * (i % 2)}>
            <div
              data-testid={`video-${i}`}
              className="overflow-hidden rounded-2xl border border-white/10"
            >
              <video
                src={video}
                controls
                playsInline
                preload="metadata"
                className="aspect-video w-full bg-black/40 object-contain"
              />
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const WaitingSection = () => (
  <section data-testid="waiting-section" className="border-t border-white/10 py-16 md:py-20">
    <div className="mx-auto max-w-4xl px-5 text-center md:px-10">
      <Reveal>
        <h2 className="font-display text-4xl uppercase leading-[0.95] tracking-tight text-white md:text-6xl">
          What are you waiting for?
        </h2>
        <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-white/70 md:text-lg">
          Either you're a competitor drooling over our prices and wondering how we do it, or
          you're a smart customer who doesn't want to pay a house deposit for a bathroom.
        </p>
        <p className="mx-auto mt-4 max-w-xl text-sm italic text-white/55 md:text-base">
          (If it's the first one — we'll never tell. If it's the second — let's talk.)
        </p>
      </Reveal>
    </div>
  </section>
);
const TimelineSection = () => (
  <section data-testid="timeline-section" className="border-t border-white/10 py-14 md:py-16">
    <div className="mx-auto max-w-5xl px-5 md:px-10">
      <Reveal>
        <div className="rounded-3xl border border-sdh-sky/30 bg-sdh-sky/[0.08] p-8 text-center md:p-12">
          <Clock size={32} className="mx-auto text-sdh-sky" />
          <h2 className="mt-4 font-display text-4xl uppercase leading-[0.95] tracking-tight text-white md:text-6xl">
            3 Weeks. Guaranteed.
          </h2>
          <p className="mx-auto mt-4 max-w-lg text-base leading-relaxed text-white/70 md:text-lg">
            From strip-out to handover, your complete bathroom renovation is finished in three
            weeks — locked in before we start.
          </p>
        </div>
      </Reveal>
    </div>
  </section>
);

const PackagePage = ({ packageId }) => {
  const pkg = PACKAGES[packageId];
  const content = PACKAGE_CONTENT[packageId];
  const { withTracking } = useTracking();

  return (
    <main data-testid={`package-page-${packageId}`}>
      <section className="relative overflow-hidden border-b border-white/10 pb-14 pt-28 md:pb-20 md:pt-36">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-40 right-[-15%] h-[480px] w-[480px] rounded-full bg-sdh-sky/15 blur-3xl"
        />
        <div className="relative mx-auto max-w-5xl px-5 md:px-10">
          <Link
            to={withTracking("/")}
            data-testid="back-to-packages-link"
            className="inline-flex items-center gap-2 text-sm font-medium text-white/60 transition-colors duration-300 hover:text-sdh-sky"
          >
            <ArrowLeft size={15} /> Back to packages
          </Link>
          <div className="mt-8 text-center">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-sdh-sky">
              {content.eyebrow}
            </p>
            <h1 className="mx-auto mt-3 max-w-3xl font-display text-5xl leading-[0.92] tracking-tight text-white md:text-7xl">
              <MaskLine delay={0.1}>{pkg.name}</MaskLine>
            </h1>
            <MaskLine delay={0.2}>
              <p className="mx-auto mt-4 max-w-2xl text-sm italic leading-relaxed text-white/55 md:text-base">
                "Cheaper than your cousin's mate who 'knows a guy' — and we actually show up."
              </p>
            </MaskLine>
            <MaskLine delay={0.28}>
              <p className="mx-auto mt-3 max-w-lg text-base text-white/70 md:text-lg">{pkg.tagline}</p>
            </MaskLine>
            <MaskLine delay={0.42}>
              <div className="mt-6 flex items-end justify-center gap-3">
                <p
                  className="font-display text-6xl tracking-tight text-sdh-sky md:text-7xl"
                  data-testid="package-page-price"
                >
                  {pkg.price}
                </p>
                <span
                  data-testid="package-page-gst"
                  className="mb-2 rounded-full bg-sdh-sky/15 px-3.5 py-1.5 font-display text-xl uppercase tracking-wide text-sdh-sky"
                >
                  + GST
                </span>
              </div>
            </MaskLine>
            <MaskLine delay={0.5}>
              <GSTNote />
            </MaskLine>
            <Reveal delay={0.6}>
              <div className="mt-7 flex justify-center">
                <button
                  onClick={() =>
                    document
                      .querySelector('[data-testid="enquiry-form"]')
                      ?.scrollIntoView({ behavior: "smooth", block: "center" })
                  }
                  data-testid="package-book-now-button"
                  className="cta-pulse-sky inline-flex items-center gap-3 rounded-full bg-sdh-sky px-9 py-4 font-display text-xl uppercase tracking-wider text-[#0B1D39] transition-colors duration-300 hover:bg-white md:text-2xl"
                >
                  Book Now — Free Quote
                </button>
              </div>
            </Reveal>
            <Reveal delay={0.65}>
              <img
                src={MASCOT_FACE_URL}
                alt="Same Day Home Services tradie"
                className="mx-auto mt-8 w-28 rounded-full border-2 border-sdh-sky/50 md:w-36"
                data-testid="package-mascot"
              />
            </Reveal>
          </div>
        </div>
      </section>

      <TrustBadges />
      <InclusionsSection content={content} />
      <GallerySection />
      <VideoSection />
      <TimelineSection />

      <section className="border-t border-white/10 py-16 md:py-24">
        <div className="mx-auto max-w-5xl px-5 md:px-10">
          <Reveal>
            <div
              data-testid="enquiry-cue"
              className="mb-10 flex flex-col items-center gap-2 text-center"
            >
              <span className="font-display text-2xl uppercase tracking-wider text-sdh-sky md:text-3xl">
                Like what you see?
              </span>
              <motion.span
                animate={{ y: [0, 7, 0] }}
                transition={{ repeat: Infinity, duration: 1.5, ease: "easeInOut" }}
              >
                <ChevronDown size={26} className="text-sdh-sky" />
              </motion.span>
            </div>
          </Reveal>
        </div>
        <div className="mx-auto grid max-w-5xl items-start gap-10 px-5 md:grid-cols-2 md:px-10">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-sdh-sky">
              Enquire about this package
            </p>
            <h2 className="mx-auto mt-3 max-w-3xl text-center font-display text-4xl uppercase leading-[0.95] tracking-tight text-white md:text-6xl">
              Get the catalogue.
            </h2>
            <p className="mx-auto mt-5 max-w-md text-center text-base text-white/70 md:text-lg">
              Submit your details and we'll send you the full supplier catalogue with every PC
              item included in your package — plus a real person will be in touch to chat.
            </p>
            <div className="mt-8 flex flex-col items-center gap-4 rounded-2xl border border-sdh-sky/30 bg-sdh-sky/10 p-5 text-center md:flex-row md:text-left">
              <img
                src={MASCOT_FACE_URL}
                alt=""
                className="w-14 shrink-0 rounded-full border-2 border-sdh-sky/60"
              />
              <p className="text-sm font-medium leading-relaxed text-white/85">
                "The price we quote is the price you pay. Unlike your gym membership, this
                actually shows up."
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <EnquiryForm defaultPackage={packageId} />
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default PackagePage;
